import {
  COMMERCIAL_DOCUMENT_LABELS,
  type CommercialDocumentListSchema,
} from "../../shared/commercial-documents";
import type { CustomerDocument, CustomerDocumentKind } from "../../shared/customer-documents";
import type { OpsPermission } from "../../shared/operations-schemas";
import type { HubAssignments, ToolHub } from "../../shared/tool-hubs";
import type { z } from "zod";
import { documentStatusVariant } from "../knowledge/customer-document-format";

export type GeneratedDocument = z.infer<typeof CommercialDocumentListSchema>["documents"][number];
export type DocumentGroup = "contracts" | "knowledge";
type Variant = "default" | "secondary" | "success" | "warning";

/** One row of the Documents table, whichever store the document lives in (ADR-0088). */
export type DocumentRow = {
  key: string;
  title: string;
  detail: string;
  group: DocumentGroup;
  hubId: string | null;
  status: { label: string; variant: Variant };
  visible: string;
  updatedAt: number;
  source:
    | { kind: "library"; document: CustomerDocument }
    | { kind: "generated"; document: GeneratedDocument; signedCopy: CustomerDocument | null };
};

const AGREEMENT_TYPES = {
  msa: "Master services agreement",
  sow: "Statement of work",
  nda: "Nondisclosure agreement",
  other: "Agreement",
} as const;

const capitalized = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function libraryType(fields: CustomerDocument["draft"]["fields"]) {
  if (fields.kind === "knowledge") return "Knowledge article";
  if (fields.kind === "quote") return "Quote";
  return AGREEMENT_TYPES[fields.agreementType ?? "other"];
}

/** Whether the customer can see the document, in the words the table uses. */
export function customerVisibility(document: CustomerDocument) {
  if (document.archived) return "Archived";
  if (!document.published) return "No";
  const changed =
    JSON.stringify(document.draft.fields) !== JSON.stringify(document.published.fields);
  return changed ? "Yes · newer edits not shared" : "Yes";
}

export function libraryRow(document: CustomerDocument, hubId: string | null): DocumentRow {
  const fields = document.draft.fields;
  return {
    key: `library:${document.id}`,
    title: fields.title,
    detail: [libraryType(fields) === fields.title ? null : libraryType(fields), fields.reference]
      .filter(Boolean)
      .join(" · "),
    group: fields.kind === "knowledge" ? "knowledge" : "contracts",
    hubId,
    status:
      fields.kind === "knowledge"
        ? document.published
          ? { label: "Published", variant: "success" }
          : { label: "Draft", variant: "secondary" }
        : { label: capitalized(fields.status), variant: documentStatusVariant(fields.status) },
    visible: customerVisibility(document),
    updatedAt: document.updatedAt,
    source: { kind: "library", document },
  };
}

/**
 * One row per contract: a generated document carries the signed copy filed from it, and the
 * customer can see the contract only through that copy.
 */
export function generatedRow(
  document: GeneratedDocument,
  hubId: string | null,
  signedCopy: CustomerDocument | null = null,
): DocumentRow {
  return {
    key: `generated:${document.id}`,
    title: document.title,
    detail: `${COMMERCIAL_DOCUMENT_LABELS[document.kind]} ${document.reference} · version ${document.revision}`,
    group: "contracts",
    hubId,
    status: document.filing
      ? { label: "Signed", variant: "success" }
      : { label: "Draft", variant: "secondary" },
    visible: signedCopy ? customerVisibility(signedCopy) : "No",
    updatedAt: document.createdAt,
    source: { kind: "generated", document, signedCopy },
  };
}

/** Both stores merged, newest first, each row carrying the tool it belongs to. */
export function documentRows(input: {
  library: readonly CustomerDocument[];
  generated: readonly GeneratedDocument[];
  assignments: HubAssignments | undefined;
}): DocumentRow[] {
  const library = new Map(input.assignments?.library.map((row) => [row.documentId, row.hubId]));
  const generated = new Map(
    input.assignments?.commercial.map((row) => [row.documentId, row.hubId]),
  );
  const loaded = new Map(input.library.map((document) => [document.id, document]));
  const copies = new Set(input.generated.map((document) => document.filing?.customerDocumentId));
  return [
    ...input.library
      .filter((document) => !copies.has(document.id))
      .map((document) => libraryRow(document, library.get(document.id) ?? null)),
    ...input.generated.map((document) =>
      generatedRow(
        document,
        generated.get(document.id) ?? null,
        loaded.get(document.filing?.customerDocumentId ?? "") ?? null,
      ),
    ),
  ].sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Where a stored document can be read as-is: its PDF, or its HTTPS link. Pages open in the reader. */
export function documentViewUrl(slug: string, document: CustomerDocument) {
  const source = document.draft.fields.source;
  if (source.type === "file")
    return `/api/ops/customers/${encodeURIComponent(slug)}/document-preview/${encodeURIComponent(document.id)}/file`;
  return source.type === "link" ? source.url : null;
}

/** The library document shown in the side sheet; `fromRow` is the contract it was opened from. */
export type DocumentDialog = {
  kind: CustomerDocumentKind;
  document?: CustomerDocument;
  fromRow?: string;
} | null;

/** What the employee may do on the Documents tab. */
export function abilities(permissions: ReadonlySet<OpsPermission>) {
  return {
    readKnowledge: permissions.has("context.read"),
    writeKnowledge: permissions.has("context.write"),
    readCommercial: permissions.has("billing.read"),
    manageCommercial: permissions.has("billing.manage"),
    usePages: permissions.has("docs.read") && permissions.has("docs.write"),
  };
}
export type DocumentAbilities = ReturnType<typeof abilities>;

/** The tool a document belongs to, or the whole company. */
export function hubName(hubs: readonly ToolHub[], hubId: string | null) {
  if (!hubId) return "Whole company";
  return hubs.find((hub) => hub.id === hubId)?.name ?? "A tool";
}
